/**
 * Soil-moisture-balance model.
 *
 * We track a per-zone water "deficit" — how far below field capacity the root
 * zone has dropped. Each day the deficit grows by crop ET and shrinks with
 * effective rainfall and irrigation. When the deficit reaches the allowed
 * depletion threshold, the zone is due for water, and we apply enough to
 * refill it to field capacity.
 */
import { wettedFraction } from './drip.js';
import { AVAILABLE_WATER_CAPACITY_IN_PER_IN } from './tables.js';
/**
 * Total plant-available water in the managed root zone, inches (canopy-referenced).
 * = available-water-capacity (in/in) × root depth (in) × wetted fraction.
 *
 * For spray/rotor the wetted fraction is 1 (whole area wetted). For drip it is
 * f < 1: the emitters wet only part of the canopy, so the reservoir the plant
 * can draw on is proportionally smaller — which is why drip waters in short,
 * frequent runs. This is THE single f-injection point; everything that measures
 * the bucket (allowedDepletionIn, advanceDeficit, deficitFromMoisture) flows
 * through it. See docs/specs/drip-water-model.md.
 */
export function totalAvailableWaterIn(zone) {
    return AVAILABLE_WATER_CAPACITY_IN_PER_IN[zone.soilType] * zone.rootDepthIn * wettedFraction(zone);
}
/**
 * The deficit (inches) at which the zone should be watered — the management
 * allowed depletion fraction of the total available water.
 */
export function allowedDepletionIn(zone) {
    return totalAvailableWaterIn(zone) * zone.allowedDepletionFraction;
}
/**
 * Advance the soil-moisture deficit by one day.
 *
 * newDeficit = clamp(prevDeficit + ETc − effectiveRain×f − irrigation, 0, TAW)
 *
 * Floored at 0 (excess water drains away rather than becoming "credit") and
 * capped at total available water (a fully depleted soil can't lose more of
 * the water we manage).
 *
 * Rain is scaled by the wetted fraction f: for drip, only rain falling on the
 * wetted footprint reaches the concentrated root zone we manage. This is the
 * single rain-scaling point — every balance caller (daily update, projection,
 * backend) inherits it. ET (canopy demand) and irrigation (already a
 * canopy-referenced net depth) are unscaled; f is 1 for non-drip zones so this
 * is a no-op there. See docs/specs/drip-water-model.md §6.1.
 */
export function advanceDeficit(params) {
    const { zone, previousDeficitIn, cropEtIn, effectiveRainIn, irrigationIn } = params;
    const next = previousDeficitIn + cropEtIn - effectiveRainIn * wettedFraction(zone) - irrigationIn;
    const taw = totalAvailableWaterIn(zone);
    return Math.min(Math.max(next, 0), taw);
}
/**
 * The ET-only deficit carried on a balance, defaulting to the live deficit.
 *
 * A balance written before `modeledDeficitIn` existed has no shadow value, and
 * the live deficit is the best (and only) estimate we have of it. Seeding from
 * it here rather than from 0 means an upgraded tenant starts the shadow at
 * today's belief rather than at "soil is full", which would suppress the
 * disagreement check for days.
 */
function shadowOf(balance) {
    return balance.modeledDeficitIn ?? balance.deficitIn;
}
/**
 * Credit applied water to both the live and the ET-only deficit.
 *
 * The single place water is subtracted from a balance. It exists because the
 * shadow deficit is only as trustworthy as the water credited against it: one
 * hand-rolled `Math.max(0, deficitIn - net)` that forgets the shadow makes the
 * zone look permanently parched to the disagreement check.
 */
export function creditBalanceWater(balance, netAppliedIn) {
    return {
        ...balance,
        deficitIn: Math.max(0, balance.deficitIn - netAppliedIn),
        modeledDeficitIn: Math.max(0, shadowOf(balance) - netAppliedIn),
    };
}
/**
 * Apply a signed first-order weather correction (positive = drier) to both
 * deficits. Used by the archive reconciliation, which corrects an observed day
 * after the fact; see the planner's `reconcileObservedWeather`.
 */
export function applyBalanceWeatherDelta(balance, deltaIn) {
    return {
        ...balance,
        deficitIn: Math.max(0, balance.deficitIn + deltaIn),
        modeledDeficitIn: Math.max(0, shadowOf(balance) + deltaIn),
    };
}
/** Whether the zone's deficit has reached the point where it needs water. */
export function isDueForWater(zone, deficitIn) {
    return deficitIn >= allowedDepletionIn(zone);
}
/**
 * Net depth of water to apply to refill the zone to field capacity, inches.
 * (Equal to the current deficit; efficiency is applied separately to get the
 * gross amount the sprinklers must deliver.)
 */
export function netDepthToRefillIn(deficitIn) {
    return Math.max(0, deficitIn);
}
/**
 * Gross depth the sprinklers must deliver to achieve `netDepthIn` at the root
 * zone, accounting for irrigation efficiency (distribution uniformity + losses).
 */
export function grossDepthIn(netDepthIn, efficiency) {
    if (efficiency <= 0)
        return 0;
    return netDepthIn / efficiency;
}
/**
 * Convert a sensor moisture reading into a deficit (inches) using the zone's
 * calibration bounds: at `moistureHighPct` the soil is full (deficit 0), at
 * `moistureLowPct` it is exactly due for water (deficit = allowed depletion),
 * scaling linearly and clamped to [0, total available water]. Returns
 * undefined when the zone has no usable calibration.
 */
export function deficitFromMoisture(zone, moisturePct) {
    const low = zone.moistureLowPct;
    const high = zone.moistureHighPct;
    if (low === undefined || high === undefined || high <= low)
        return undefined;
    const fraction = (high - moisturePct) / (high - low);
    const deficit = fraction * allowedDepletionIn(zone);
    return Math.min(Math.max(deficit, 0), totalAvailableWaterIn(zone));
}
//# sourceMappingURL=soil.js.map